// FreePreviewCTA — captures visitor email into MongoDB `leads` collection via
// POST /api/leads/preview. No transactional email is sent automatically:
// marketing follow-up is handled manually by the admin.

import { useState } from 'react';
import { Sparkles, Mail, Check } from 'lucide-react';
import useTranslation from '../data/useTranslation';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency } from '../contexts/CurrencyContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function FreePreviewCTA() {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const { currency } = useCurrency();
  const l = t.landing;

  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!EMAIL_RE.test(email)) {
      setError(l.previewInvalid);
      return;
    }
    if (!consent) {
      setError(l.previewMustConsent);
      return;
    }

    setSubmitting(true);

    // Analytics (non-PII: only the domain part of the email)
    try {
      const emailDomain = email.split('@')[1] || '';
      if (window.posthog && typeof window.posthog.capture === 'function') {
        window.posthog.capture('preview_lead_captured', {
          language,
          currency,
          email_domain: emailDomain,
        });
      }
    } catch (e) { /* ignore */ }

    let backendOk = false;
    try {
      const res = await fetch(`${API_URL}/api/leads/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.toLowerCase(), language }),
      });
      backendOk = res.ok;
    } catch (_err) {
      backendOk = false;
    }

    if (backendOk) {
      setSuccess(true);
    } else {
      setError(l.previewGenericError || l.previewInvalid);
    }
    setSubmitting(false);
  };

  return (
    <section className="py-12" data-testid="free-preview-section">
      <div className="container mx-auto px-6">
        <div className="max-w-2xl mx-auto p-8 rounded-2xl bg-gradient-to-br from-orange-500/10 to-purple-500/10 border border-orange-500/30 shadow-xl text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-orange-500/20 mb-4">
            <Sparkles className="w-7 h-7 text-orange-400" />
          </div>
          <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2" data-testid="preview-title">{l.previewTitle}</h3>
          <p className="text-gray-300 mb-6">{l.previewSub}</p>

          {success ? (
            <div
              className="p-4 rounded-xl bg-green-500/10 border border-green-500/30 text-green-300 flex items-center justify-center gap-2"
              data-testid="preview-success"
            >
              <Check className="w-5 h-5" />
              <span>{l.previewSuccess}</span>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4 text-left" noValidate>
              <div className="relative">
                <Mail className="w-5 h-5 text-gray-500 absolute left-4 top-1/2 -translate-y-1/2" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={l.previewEmailPlaceholder}
                  className="w-full pl-12 pr-4 py-3 bg-[#111827] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none transition-colors"
                  data-testid="preview-email-input"
                />
              </div>

              <label className="flex items-start gap-3 cursor-pointer text-xs text-gray-400 leading-snug">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-orange-500 cursor-pointer flex-shrink-0"
                  data-testid="preview-consent"
                />
                <span>{l.previewConsent}</span>
              </label>

              {error && (
                <p className="text-sm text-red-400" data-testid="preview-error">{error}</p>
              )}

              <button
                type="submit"
                disabled={submitting}
                data-testid="preview-submit"
                className="w-full py-3 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-xl font-semibold text-white transition-all flex items-center justify-center gap-2"
              >
                <Sparkles className="w-4 h-4" />
                {l.previewBtn}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

export default FreePreviewCTA;
